'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { type FieldValues, useForm } from 'react-hook-form';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { generateZodSchema } from '../compiler';
import { fieldRegistry } from '../registry';
import { FormRenderer } from '../renderers/form-renderer';
import type { FormFieldSchema } from '../types';

interface FieldConfigModalProps {
  isOpen: boolean;
  field: FormFieldSchema;
  onClose: () => void;
  onSave: (field: FormFieldSchema) => void;
}

export function FieldConfigModal({ isOpen, field, onClose, onSave }: FieldConfigModalProps) {
  const definition = fieldRegistry.get(field.type);
  const configSchema = useMemo(() => definition?.configSchema || [], [definition]);
  const fieldTypeLabel = definition?.toolboxLabel || field.type;

  const validationSchema = useMemo(() => generateZodSchema(configSchema), [configSchema]);

  const { control, handleSubmit, reset } = useForm<FieldValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: field as unknown as FieldValues,
    mode: 'onTouched',
  });

  // Reset form when field changes
  useEffect(() => {
    if (isOpen) {
      reset(field as unknown as FieldValues);
    }
  }, [field, reset, isOpen]);

  const handleSave = (data: FieldValues) => {
    onSave(data as unknown as FormFieldSchema);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Configure {fieldTypeLabel}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleSave)} className="space-y-6 py-4">
          <FormRenderer fields={configSchema} mode="edit" control={control} className="gap-6" />

          <DialogFooter className="gap-2 border-t pt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
